import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";

import { DaysWeek } from "../DaysWeek";
import { SelectRestaurants } from "../Modals/SelectRestaurants";
import { Form } from "../../../components/Form";
import { Input } from "../../../components/Input";
import { FormField } from "../../../components/FormField";
import { format } from "date-fns";
import { useParams } from "react-router";
import { MenuPageHeader } from "../MenuPageHeader";
import Loading from "../../../components/Loading";
import {
    useGetAllRestorants,
    useGetMenu,
    useCreateNewMenu,
    useEditMenu,
} from "../../../network";
import { CustomSelect } from '../../../components/Shared/CustomSelect';

export const AddMenu = () => {
    const { t } = useTranslation();
    const { slug } = useParams();

    const [pages, setPages] = useState([
        { name: t("Menu"), path: "/chain/menu" },
    ]);

    const { data: { data: restaurants = [] } = {}, isLoading } = useGetAllRestorants();
    const {
        data: menuData,
        isLoading: isMenuLoading,
        fetchStatus,
    } = useGetMenu({ slug });

    const { mutate: createNewMenu, isLoading: isAddingNewMenu } = useCreateNewMenu();
    const { mutate: editCurrentMenu, isLoading: isEditingCurrentMenu } = useEditMenu();

    const menuInformation = menuData?.data;

    const [restaurantsDefaultOptions, setRestaurantsDefaultOptions] = useState(
        []
    );

    const [openModal, setOpenModal] = useState(false);
    const [isCheckAllFutureLocations, setIsCheckAllFutureLocations] =
        useState(false);

    const validationSchema = yup.object().shape({
        name: yup.string().required(t("Required!")),
        store_ids: yup.array().min(1, t("Required!")),
    });

    const formik = useFormik({
        initialValues: {
            store_ids: [],
            name: "",
            start_date: "",
            end_date: "",
            times: [],
        },
        enableReinitialize: true,
        validateOnChange: false,
        validationSchema: validationSchema,
        onSubmit: ({ end_date, name, start_date, store_ids, times }) => {
            const serialized = {
                name,
                start_date:
                    start_date &&
                    format(new Date(start_date), "yyyy-MM-dd HH:mm:ss"),
                end_date:
                    end_date &&
                    format(new Date(end_date), "yyyy-MM-dd HH:mm:ss"),
                store_ids: store_ids.map((id) => Number(id)),
                times,
            };
            if (slug) {
                editCurrentMenu({ params: serialized, slug });
            } else {
                createNewMenu({ params: serialized });
            }
        },
    });

    const isAllDisabled =
        isAddingNewMenu ||
        (isMenuLoading && fetchStatus === "fetching") ||
        isEditingCurrentMenu || menuInformation?.status === "archived";

    const isAllChecked = restaurants.every((el) =>
        formik.values.store_ids.includes(String(el.id))
    );

    const onRestaurantsSelectChange = (_, actionMeta) => {
        const id = actionMeta.removedValue.id;
        setRestaurantsDefaultOptions((prev) =>
            prev.filter((el) => String(el.id) !== String(id))
        );
        formik.setFieldValue(
            "store_ids",
            formik.values.store_ids.filter((v) => v !== String(id))
        );
    };

    const daysWeek = [
        { title: t("Mon"), name: "monday", weekday: 1 },
        { title: t("Tue"), name: "tuesday", weekday: 2 },
        { title: t("Wed"), name: "wednesday", weekday: 3 },
        { title: t("Thur"), name: "thursday", weekday: 4 },
        { title: t("Fri"), name: "friday", weekday: 5 },
        { title: t("Sat"), name: "saturday", weekday: 6 },
        { title: t("Sun"), name: "sunday", weekday: 7 },
    ];

    const onOpenModal = (e) => {
        if (isAllDisabled) {
            return;
        }
        const parentNode = e.target.parentNode;
        if (
            parentNode.tagName !== "svg" &&
            !parentNode.className?.includes("select__multi-value")
        ) {
            setOpenModal((prev) => !prev);
        }
    };
    const onCloseModal = () => {
        setOpenModal(false);
        const restaurantsIds = restaurantsDefaultOptions.map((el) =>
            String(el.id)
        );
        formik.setFieldValue("store_ids", restaurantsIds);
    };
    const onSaveLocations = () => {
        const newOptions = restaurants.filter((el) =>
            formik.values.store_ids.includes(String(el.id))
        );
        const optionsMap = newOptions.map(({ name, id }) => ({
            name,
            label: name,
            id,
        }));
        setRestaurantsDefaultOptions(optionsMap);
        setOpenModal(false);
    };

    const handleChange = (e) => {
        formik.handleChange(e);
    };

    const handleSelectAll = () => {
        if (isAllChecked) {
            formik.setFieldValue("store_ids", []);
        } else {
            const restaurantsIds = restaurants.map((el) => String(el.id));
            formik.setFieldValue("store_ids", restaurantsIds);
        }
    };

    const handleSelectAllFutureLocations = () => {
        setIsCheckAllFutureLocations(!isCheckAllFutureLocations);
    };

    useEffect(() => {
        if (menuInformation) {
            const { restaurants, name, start_date, end_date, id, times } =
                menuInformation;
            setPages(() => [
                { name: t("Menu"), path: "/chain/menu" },
                {
                    name: name,
                    path: `/chain/menu/${id}/edit`,
                },
            ]);

            formik.setValues({
                store_ids: restaurants
                    ? restaurants.map((el) => String(el.id))
                    : [],
                name: name ?? "",
                start_date: start_date ? new Date(start_date) : "",
                end_date: end_date ? new Date(end_date) : "",
                times: times ?? [],
            });
            const optionsMap =
                restaurants?.map(({ name, id }) => ({
                    name,
                    label: name,
                    id,
                })) || [];
            setRestaurantsDefaultOptions(optionsMap);
        }
    }, [menuInformation]);

    return (
        <div className="max-w-screen-lg px-4 sm:px-8 lg:px-10 xl:px-12">
        <Loading loading={isMenuLoading && fetchStatus === "fetching"}>
            <Form
                handleSubmit={formik.handleSubmit}
                className="grid grid-cols-6"
            >
                <MenuPageHeader
                    isAllDisabled={isAllDisabled}
                    isAddingNewMenu={isAddingNewMenu}
                    isEditingCurrentMenu={isEditingCurrentMenu}
                    pages={pages}
                    setPages={setPages}
                />

                <div className="grid grid-cols-6 col-span-6 rounded-lg shadow-md bg-white px-4">
                    <div className="col-span-6 flex flex-col md:flex-row justify-between gap-2 md:gap-6">
                        <div className="w-full py-2.5">
                            <FormField
                                label={t("Name")}
                                htmlFor="name"
                                required
                                error={formik.errors.name}
                                disabled={isAllDisabled}
                                popoverText={t("menu is archived")}
                            >
                                <Input
                                    id="name"
                                    type="text"
                                    name="name"
                                    disabled={isAllDisabled}
                                    onChange={handleChange}
                                    value={formik.values.name}
                                    placeholder={t("Name")}
                                />
                            </FormField>
                        </div>
                        <div className="w-full py-2.5">
                            <FormField
                                label={t("Publish to Stores")}
                                htmlFor="store_ids"
                                required
                                error={
                                    restaurantsDefaultOptions.length === 0 &&
                                    formik.errors.store_ids
                                }
                                disabled={isAllDisabled}
                                popoverText={t("menu is archived")}
                            >
                                <div onClick={onOpenModal}>
                                    <CustomSelect
                                        placeholder={t("Select Stores")}
                                        isMulti
                                        isSearchable={false}
                                        isClearable={false}
                                        isDisabled={isAllDisabled}
                                        components={{
                                            Menu: () => null,
                                            MenuList: () => null,
                                        }}
                                        className="basic-multi-select"
                                        classNamePrefix="select"
                                        value={restaurantsDefaultOptions}
                                        onChange={onRestaurantsSelectChange}
                                    />
                                </div>
                            </FormField>
                        </div>

                        <SelectRestaurants
                            isLoading={isLoading}
                            visible={openModal}
                            onClose={onCloseModal}
                            onSave={onSaveLocations}
                            onChangeRestaurants={handleChange}
                            formikValues={formik.values.store_ids}
                            isAllChecked={isAllChecked}
                            isCheckAllFutureLocations={
                                isCheckAllFutureLocations
                            }
                            restaurants={restaurants}
                            handleSelectAll={handleSelectAll}
                            handleSelectAllFutureLocations={
                                handleSelectAllFutureLocations
                            }
                            restaurantName="store_ids"
                        />
                    </div>

                    <div className="col-span-6 md:col-span-4 lg:col-span-3 pr-3 mt-7">
                        {daysWeek?.map(({ name, title, weekday }) => (
                            <DaysWeek
                                handleChange={handleChange}
                                setTimes={formik.setFieldValue}
                                times={formik.values.times}
                                isArchived={menuInformation?.status === "archived"}
                                key={title}
                                isAllDisabled={isAllDisabled}
                                title={title}
                                dayName={name}
                                weekday={weekday}
                            />
                        ))}
                    </div>

                    <div className="col-span-6 lg:pr-40 mt-7 flex items-start">
                        <img src="/menuicon/POS_info.svg" alt="info" />
                        <span className="text-xs font-normal italic ml-1">
                            {t(
                                "*Optional: You may also set a start and end date for your menu. (Example: for a Christmas menu you might set a start date of Dec 11 and end date of Jan 5)"
                            )}
                        </span>
                    </div>

                    <div className="col-span-6 pr-3 mt-3 mb-10 flex justify-between items-center gap-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                            <FormField
                                label={t("Start Date")}
                                htmlFor="start_date"
                                disabled={isAllDisabled}
                                popoverText={t("menu is archived")}
                            >
                                <DatePicker
                                    name="start_date"
                                    selected={formik.values.start_date}
                                    placeholderText={t("Start Date")}
                                    onChange={(value) => {
                                        formik.setFieldValue(
                                            "start_date",
                                            value
                                        );
                                    }}
                                    className="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm bg-gray-50 focus:ring-gray-300 focus:border-gray-200"
                                    readOnly={isAllDisabled}
                                />
                            </FormField>
                            <FormField label={t("End Date")}
                                       htmlFor="end_date"
                                       disabled={isAllDisabled}
                                       popoverText={t("menu is archived")}
                            >
                                <DatePicker
                                    name="end_date"
                                    placeholderText={t("End Date")}
                                    selected={formik.values.end_date}
                                    onChange={(value) => {
                                        formik.setFieldValue("end_date", value);
                                    }}
                                    className="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm bg-gray-50 focus:ring-gray-300 focus:border-gray-200"
                                    minDate={formik.values.start_date}
                                    readOnly={isAllDisabled}
                                />
                            </FormField>
                        </div>
                    </div>
                </div>
            </Form>
        </Loading>
        </div>
    );
};
